import * as piexif from 'piexifjs';

export interface ExifEmbedFields {
  make?: string;
  model?: string;
  software?: string;
  imageDescription?: string;
  /** EXIF datetime: 'YYYY:MM:DD HH:MM:SS'. */
  dateTimeOriginal?: string;
  pixelXDimension?: number;
  pixelYDimension?: number;
  /** EXIF Orientation (1..8). */
  orientation?: number;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as string);
    r.onerror = () => rej(r.error);
    r.readAsDataURL(blob);
  });
}

/** Zero-pad to width 2. */
function p2(n: number): string {
  return n < 10 ? '0' + n : String(n);
}

/** Format a Date as the EXIF 'YYYY:MM:DD HH:MM:SS' string (UTC). */
export function formatExifDateTime(d: Date): string {
  return (
    `${d.getUTCFullYear()}:${p2(d.getUTCMonth() + 1)}:${p2(d.getUTCDate())} ` +
    `${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}:${p2(d.getUTCSeconds())}`
  );
}

/**
 * Insert the given EXIF fields into a JPEG. Returns a new JPEG Blob.
 * JPEG-only (piexif limitation); callers pass a JPEG blob.
 */
export async function embedExifIntoJpegBlob(
  jpeg: Blob,
  fields: ExifEmbedFields,
): Promise<Blob> {
  const dataUrl = await blobToDataUrl(jpeg);
  const zeroth: Record<number, unknown> = {};
  const exif: Record<number, unknown> = {};

  if (fields.make) zeroth[piexif.ImageIFD['Make']] = fields.make;
  if (fields.model) zeroth[piexif.ImageIFD['Model']] = fields.model;
  if (fields.software) zeroth[piexif.ImageIFD['Software']] = fields.software;
  if (fields.imageDescription) zeroth[piexif.ImageIFD['ImageDescription']] = fields.imageDescription;
  if (fields.orientation) zeroth[piexif.ImageIFD['Orientation']] = fields.orientation;
  if (fields.dateTimeOriginal) exif[piexif.ExifIFD['DateTimeOriginal']] = fields.dateTimeOriginal;
  if (fields.pixelXDimension) exif[piexif.ExifIFD['PixelXDimension']] = fields.pixelXDimension;
  if (fields.pixelYDimension) exif[piexif.ExifIFD['PixelYDimension']] = fields.pixelYDimension;

  const exifStr = piexif.dump({ '0th': zeroth, Exif: exif, GPS: {} } as any);
  const inserted = piexif.insert(exifStr, dataUrl);
  const resp = await fetch(inserted);
  return await resp.blob();
}
